import axios from "axios";
import Bugsnag from "@bugsnag/js";

import { delay } from "@helpers/utils";

// CLASSES
import Settings from "@classes/Settings";

const NODE_ENV = import.meta.env.VITE_NODE_ENV;
const AXIOSTIMEOUT = import.meta.env.VITE_AXIOS_TIMEOUT;
const MAX_NETWORK_RETRIES = 3;
const NETWORK_RETRY_DELAY_MS = 2000;

let API_URL = import.meta.env.VITE_API_URL;
if (API_URL === "/") {
  // If we set the api url we will use the same host on port 80 as the api server
  API_URL = window.location.protocol + "//" + window.location.hostname;
}

if (NODE_ENV === "development") {
  const apiServerPort = import.meta.env.VITE_API_SERVER_PORT;
  API_URL = apiServerPort ? API_URL + ":" + apiServerPort : API_URL;
}

// Helper function to check if an error is a network error from Axios (e.g., timeout, network issues)
const isAxiosNetworkError = error => {
  return (
    error?.name === "AxiosError" &&
    (error?.message === "Network Error" || error?.code === "ERR_NETWORK" || error?.code === "ECONNABORTED")
  );
};

const handleErrorResponse = (data, reject) => {
  if (data.error) {
    const errorMessage = data.error.message || data.error[0].message;
    const error = new Error(errorMessage || "UNKNOWN ERROR");
    switch (errorMessage) {
      case "USER_NOT_FOUND":
      case "USER_ALREADY_EXISTS":
      case "RESELLER_ALREADY_EXISTS":
      case "INVALID_OTP":
      case "OTP_VERIFICATION_FAILED":
      case "OTP_ON_COOLDOWN":
      case "MAX_ATTEMPTS_REACHED":
        // Expected error handled in the component, no action needed here
        break;
      case "USER_NOT_ACTIVE":
      case "AUTH_TOKEN_REJECTED":
      case "AUTH_TOKEN_UNDEFINED":
      case "AUTH_TOKEN_INVALID":
        console.log("Clearing user session...");
        Settings.clear();
        window.location = "/?sessionTimeout=1";
        break;
      default:
        Bugsnag.notify(error);
        reject(error);
        break;
    }
  }
};

export default function apiRequest(path, data, props = {}, returnRaw = false) {
  if (!data) {
    data = {};
  }

  const url = path[0] === "/" ? API_URL + path : API_URL + "/" + path;
  const headers = {};
  const authToken = Settings.get("AUTH_TOKEN");

  if (authToken !== null) {
    data.authToken = authToken;
  }

  const config = {
    method: "post",
    data,
    url,
    timeout: AXIOSTIMEOUT,
    ...props,
    headers: { ...headers, ...props.headers }
  };

  return new Promise((resolve, reject) => {
    const makeRequest = (retryCount = 0) => {
      axios(config)
        .then(response => {
          if (returnRaw) {
            // Check response for errors
            handleErrorResponse(response.data, reject);

            resolve(response.data);
            return;
          }

          if (typeof response.data === "string") {
            Bugsnag.notify(new Error(response.data));
            reject(new Error(response.data));
            return;
          }

          if (response.data.success === true) {
            resolve(response.data.result);
          } else {
            handleErrorResponse(response.data, reject);
          }
        })
        .catch(async error => {
          if (isAxiosNetworkError(error) && retryCount < MAX_NETWORK_RETRIES) {
            // Log the network error and retry after a delay
            console.warn(`Network error occurred. Retrying request (${retryCount + 1}/${MAX_NETWORK_RETRIES})...`, error);
            await delay(NETWORK_RETRY_DELAY_MS);            
            return makeRequest(retryCount + 1);
          }

          if (error?.response?.data) {
            return handleErrorResponse(error.response.data, reject);
          }

          Bugsnag.notify(new Error(error));
          reject(error);
        });
    };

    makeRequest();
  });
}
